import { useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { Avatar } from "./Avatar";
import type { LeaderboardEntry } from "../state";

const CONFETTI_COLORS = ["#fde68a", "#ef4444", "#22c55e", "#3b82f6", "#a855f7", "#f97316", "#ec4899", "#14b8a6"];

export function CelebrationModal({ leaderboard }: { leaderboard: LeaderboardEntry[] }) {
  const podium = leaderboard.slice(0, 3);
  const [open, setOpen] = useState(podium.length >= 3);
  const [celebrating, setCelebrating] = useState(false);
  const particles = useMemo(() => Array.from({ length: 44 }, (_, i) => {
    const angle = (i / 44) * Math.PI * 2;
    const distance = 112 + (i % 5) * 26;
    return {
      x: `${Math.cos(angle) * distance}px`,
      y: `${Math.sin(angle) * distance}px`,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      delay: `${(i % 8) * 45}ms`,
      duration: `${820 + (i % 5) * 90}ms`,
      rotate: `${120 + (i % 7) * 46}deg`,
      width: `${6 + (i % 3) * 3}px`,
      height: i % 4 === 0 ? `${6 + (i % 2) * 2}px` : `${14 + (i % 3) * 4}px`,
      radius: i % 4 === 0 ? "50%" : "2px",
    };
  }), []);

  if (podium.length < 3) return null;

  const close = () => {
    if (celebrating) return;
    setOpen(false);
  };

  const raiseTrophy = () => {
    if (celebrating) return;
    setCelebrating(true);
    window.setTimeout(() => setOpen(false), 1500);
  };

  return (
    <>
      <div className="celebration-bg" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
      {open && (
        <div className="celebration-backdrop" role="presentation" onClick={close}>
          <section
            className={`celebration-card ${celebrating ? "celebration-card--bursting" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="celebration-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="trophy-burst" aria-hidden="true">
              {particles.map((particle, i) => (
                <span
                  key={`confetti-${i}`}
                  style={{
                    "--burst-x": particle.x,
                    "--burst-y": particle.y,
                    "--burst-delay": particle.delay,
                    "--burst-duration": particle.duration,
                    "--burst-rotate": particle.rotate,
                    "--confetti-color": particle.color,
                    "--confetti-width": particle.width,
                    "--confetti-height": particle.height,
                    "--confetti-radius": particle.radius,
                  } as CSSProperties}
                />
              ))}
            </div>

            <div className="celebration-card__burst" aria-hidden="true">✦</div>
            <p className="celebration-card__eyebrow">Final whistle party</p>
            <h2 id="celebration-title">Congratulations, podium legends!</h2>
            <p className="celebration-card__copy">
              The dust has settled, the bragging rights are printed, and these three finished on top.
            </p>

            <div className="podium">
              {podium.map((entry, index) => (
                <PodiumPlayer key={entry.player_id} entry={entry} place={index + 1} />
              ))}
            </div>

            <button className="celebration-card__button" type="button" onClick={raiseTrophy} disabled={celebrating}>
              {celebrating ? "Champions crowned!" : "Raise the trophy 🏆"}
            </button>
          </section>
        </div>
      )}
    </>
  );
}

function PodiumPlayer({ entry, place }: { entry: LeaderboardEntry; place: number }) {
  const medals = ["🥇", "🥈", "🥉"];
  return (
    <div className={`podium__player podium__player--${place}`}>
      <span className="podium__medal" aria-label={`Rank ${place}`}>{medals[place - 1]}</span>
      <Avatar name={entry.name} initials={entry.initials} color={entry.color} size="lg" />
      <strong>{entry.name}</strong>
      <span>{entry.total} pts</span>
    </div>
  );
}
